import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Check,
  Download,
  FileText,
  Loader2,
  PackageSearch,
  Plus,
  Search,
  Receipt,
  ShieldCheck,
  Tag,
  Upload,
  Truck,
  Wallet,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import ModalBase from "components/ModalBase";
import InputDate from "components/CustomInputs/InputDate";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import ModalRealizarCotacao from "./ModalRealizarCotacao";

import {
  CobrancaCotacao,
  Cotacao,
  ResumoCotacoes,
} from "services/cotacoes/cotacoes.types";
import {
  agendarColetaCotacao,
  getCobrancaCotacao,
  getCotacoes,
  getResumoCotacoes,
  pagarCotacaoComSaldo,
} from "services/cotacoes/cotacoes.service";
import { criarBoletoCotacao } from "services/boleto";
import {
  abrirCarteira,
  avisarCarteiraAtualizada,
} from "services/carteira/carteira.service";
import { postRelatorioPagamentosCsv } from "services/relatorio";
import {
  anexarDocsCotacao,
  baixarCpscCotacao,
  baixarEtiquetaCotacao,
  baixarInvoiceCotacao,
  excluirCotacao,
  gerarCpscCotacao,
  gerarInvoiceCotacao,
} from "services/api";
import type { CpscOpcoes } from "services/api";
import { downloadCsv } from "utils/downloadCsv";
import dateToISO from "utils/dateToISO";
import { extrairUltimoEvento, tempoRelativo } from "utils/ultimoEvento";
import { corTransportadora, urlRastreio } from "utils/rastreioTransportadora";
import { useAuth } from "hooks/useAuth";

type Props = {
  show: boolean;
  title: string;
  setShowNumber: (value: React.SetStateAction<number>) => void;
  logo?: React.ReactNode;
};

/* ------------------------------------------------------------------ */
/* Status                                                              */
/* ------------------------------------------------------------------ */

const STATUS_CONFIG: Record<string, { label: string; className: string; ponto: string }> = {
  CRIADO: { label: "Criado", className: "bg-blue-50 text-blue-700", ponto: "bg-blue-500" },
  COLETADO: { label: "Coletado", className: "bg-sky-50 text-sky-700", ponto: "bg-sky-500" },
  EM_TRANSITO: { label: "Em trânsito", className: "bg-amber-50 text-amber-800", ponto: "bg-amber-500" },
  SAIU_PARA_ENTREGA: { label: "Saiu para entrega", className: "bg-indigo-50 text-indigo-700", ponto: "bg-indigo-500" },
  ENTREGUE: { label: "Entregue", className: "bg-emerald-50 text-emerald-700", ponto: "bg-emerald-500" },
  EXCECAO: { label: "Ocorrência", className: "bg-red-50 text-red-700", ponto: "bg-red-500" },
  RETORNADO: { label: "Retornado", className: "bg-orange-50 text-orange-700", ponto: "bg-orange-500" },
  ERRO: { label: "Erro", className: "bg-red-50 text-red-700", ponto: "bg-red-500" },
  CANCELADO: { label: "Cancelado", className: "bg-slate-100 text-slate-500", ponto: "bg-slate-400" },
};

const configStatus = (status: string) =>
  STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-slate-100 text-slate-500",
    ponto: "bg-slate-400",
  };

const StatusBadge = ({ status }: { status: string }) => {
  const cfg = configStatus(status);
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${cfg.className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.ponto}`} />
      {cfg.label}
    </span>
  );
};

/**
 * Abas da tela. Cada uma manda a própria lista de `status_norm` para a API,
 * que já aceita vários separados por vírgula — não há filtro no front, então
 * a paginação continua certa.
 */
const ABAS = [
  { chave: "todas", label: "Todas", status: undefined as string | undefined },
  { chave: "criadas", label: "Criadas", status: "CRIADO" },
  { chave: "transito", label: "Em trânsito", status: "COLETADO,EM_TRANSITO,SAIU_PARA_ENTREGA" },
  { chave: "entregues", label: "Entregues", status: "ENTREGUE" },
  // "ERRO" nao existe no enum status_norm do Postgres: mandar esse valor no
  // filtro nao devolve zero linhas, estoura a query com "invalid input value
  // for enum". Os validos sao CRIADO, EM_TRANSITO, ENTREGUE, EXCECAO,
  // CANCELADO, RETORNADO, SAIU_PARA_ENTREGA e COLETADO.
  { chave: "ocorrencia", label: "Com ocorrência", status: "EXCECAO,RETORNADO" },
];

/* ------------------------------------------------------------------ */
/* Formatação                                                          */
/* ------------------------------------------------------------------ */

const usd = (v: number) =>
  `USD ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const dataCurta = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
};

const dataHora = (d: Date | null) =>
  d
    ? d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

/**
 * Nome legível do serviço. A FedEx manda constante com underline
 * ("FEDEX_INTERNATIONAL_CONNECT_PLUS"); a UPS manda código numérico, que não
 * dá para adivinhar — nesse caso mostra só a transportadora.
 */
const nomeServico = (carrier: string, serviceCode?: string) => {
  const codigo = String(serviceCode ?? "").trim();
  if (!codigo) return carrier;
  if (/^\d+$/.test(codigo)) return `${carrier} · serviço ${codigo}`;

  return codigo
    .split("_")
    .filter(Boolean)
    .map((p) => p.charAt(0) + p.slice(1).toLowerCase())
    .join(" ");
};

/* ------------------------------------------------------------------ */
/* Acompanhamento                                                      */
/* ------------------------------------------------------------------ */

const ORDEM_STATUS = ["CRIADO", "COLETADO", "EM_TRANSITO", "SAIU_PARA_ENTREGA", "ENTREGUE"];

const PROXIMO_PASSO: Record<string, { label: string; dica: string }> = {
  CRIADO: { label: "Aguardando coleta", dica: "agende para a etiqueta começar a andar" },
  COLETADO: { label: "Em trânsito", dica: "a transportadora atualiza em algumas horas" },
  EM_TRANSITO: { label: "Saiu para entrega", dica: "quando chegar na praça de destino" },
  SAIU_PARA_ENTREGA: { label: "Entregue", dica: "aguardando a confirmação da entrega" },
};

/**
 * Linha do tempo do envio.
 *
 * Não é o histórico da transportadora: o backend guarda em `tracking_raw`
 * apenas o último evento, não a lista. Então os passos vêm da progressão do
 * próprio `status_norm`, com a data de criação no primeiro e o último evento
 * no passo atual. Melhor isso do que inventar horários que não existem.
 */
const passosAcompanhamento = (pedido: PedidoTabela) => {
  const evento = extrairUltimoEvento(pedido.tracking_raw);
  const fora = !ORDEM_STATUS.includes(pedido.status);

  if (fora) {
    return [
      {
        label: configStatus(pedido.status).label,
        detalhe: evento?.descricao ?? "",
        quando: dataHora(evento?.quando ?? null),
        concluido: true,
      },
    ];
  }

  const atual = ORDEM_STATUS.indexOf(pedido.status);

  const passos = ORDEM_STATUS.slice(0, atual + 1).map((s, i) => ({
    label:
      s === "CRIADO"
        ? `Etiqueta enviada à ${pedido.carrier || "transportadora"}`
        : configStatus(s).label,
    detalhe: i === atual ? (evento?.local ?? "") : "",
    quando:
      i === 0
        ? dataHora(new Date(pedido.criado_em))
        : i === atual
          ? dataHora(evento?.quando ?? null)
          : "",
    concluido: true,
  }));

  const proximo = PROXIMO_PASSO[pedido.status];
  if (proximo) {
    passos.push({
      label: proximo.label,
      detalhe: proximo.dica,
      quando: "",
      concluido: false,
    });
  }

  return passos;
};

/* ------------------------------------------------------------------ */
/* Modal                                                               */
/* ------------------------------------------------------------------ */

const ModalCotacao = ({ show, title, setShowNumber, logo }: Props) => {
  const { t } = useTranslation("dashboard");
  const { user } = useAuth();
  const safeCliente = (user?.razaoSocial || "cliente")
    .replace(/[\\/:*?"<>|]/g, "-")
    .trim();

  const [aba, setAba] = useState("todas");
  const [search, setSearch] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [inicio, setInicio] = useState<Date>();
  const [fim, setFim] = useState<Date>();
  const [carrier, setCarrier] = useState("");

  const [lista, setLista] = useState<Cotacao[]>([]);
  const [resumo, setResumo] = useState<ResumoCotacoes | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [selecionadaId, setSelecionadaId] = useState<number | null>(null);
  const [novaCotacao, setNovaCotacao] = useState(false);

  // Período do relatório de pagamentos — independente do filtro da tela.
  const [relInicio, setRelInicio] = useState<Date>();
  const [relFim, setRelFim] = useState<Date>();

  const statusDaAba = ABAS.find((a) => a.chave === aba)?.status;

  const filtrosComuns = useMemo(
    () => ({
      search: buscaAplicada || undefined,
      start_day: inicio ? dateToISO(inicio) : undefined,
      end_day: fim ? dateToISO(fim) : undefined,
      carrier: carrier || undefined,
    }),
    [buscaAplicada, inicio, fim, carrier],
  );

  const carregar = async () => {
    setCarregando(true);
    try {
      // O resumo falha sozinho de propósito. Ele depende de /cotacoes/resumo,
      // que é endpoint novo: se o front subir antes do deploy do back, um
      // Promise.all derrubaria a listagem junto e a tela ficaria vazia. Sem o
      // resumo a tela perde só os contadores das abas e o total do cabeçalho.
      const [res, res2] = await Promise.all([
        getCotacoes({ ...filtrosComuns, status: statusDaAba, page, limit }),
        getResumoCotacoes(filtrosComuns).catch(() => null),
      ]);
      setLista(res.itens);
      setTotal(res.total);
      setResumo(res2);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, page, aba, filtrosComuns]);

  // Trocar de aba ou de filtro com a página 3 aberta devolvia uma lista vazia.
  useEffect(() => {
    setPage(1);
  }, [aba, filtrosComuns]);

  useEffect(() => {
    if (show) return;
    setAba("todas");
    setSearch("");
    setBuscaAplicada("");
    setInicio(undefined);
    setFim(undefined);
    setCarrier("");
    setSelecionadaId(null);
  }, [show]);

  const pedidos = useMemo(() => extrairPedidos(lista), [lista]);

  // Seleção segue a lista: ao trocar de aba o painel não pode continuar
  // mostrando uma cotação que saiu da tela.
  useEffect(() => {
    if (!pedidos.length) {
      setSelecionadaId(null);
      return;
    }
    setSelecionadaId((atual) =>
      atual && pedidos.some((p) => p.cotacao_id === atual)
        ? atual
        : pedidos[0].cotacao_id,
    );
  }, [pedidos]);

  const selecionada = pedidos.find((p) => p.cotacao_id === selecionadaId) ?? null;

  const contarAba = (status?: string) => {
    if (!resumo) return null;
    if (!status) return resumo.total;
    return status
      .split(",")
      .reduce((acc, s) => acc + (resumo.por_status[s] ?? 0), 0);
  };

  const baixarRelatorio = async () => {
    if (!relInicio || !relFim) return;
    const blob = await postRelatorioPagamentosCsv({
      from: dateToISO(relInicio),
      to: dateToISO(relFim),
    });
    downloadCsv(blob, "relatorio-pagamentos.csv");
  };

  const totalPaginas = Math.max(1, Math.ceil(total / limit));
  const temFiltro = Boolean(buscaAplicada || inicio || fim || carrier);

  const rotuloData =
    inicio || fim
      ? `${inicio ? dataCurta(inicio.toISOString()) : "…"} – ${fim ? dataCurta(fim.toISOString()) : "…"}`
      : "Qualquer data";

  return (
    <>
      <ModalBase
        show={show}
        onClose={() => setShowNumber(-1)}
        titulo={title}
        icone={logo ?? <FileText />}
        largura="1120px"
        corpoSemPadding
        subtitulo={
          resumo
            ? `${resumo.total} ${resumo.total === 1 ? "cotação" : "cotações"} · ${usd(
                resumo.frete_desembaraco_usd,
              )} em frete e desembaraço`
            : carregando
              ? "Carregando…"
              : `${total} ${total === 1 ? "cotação" : "cotações"}`
        }
        acoes={
          <>
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50">
                  <Download className="h-4 w-4" />
                  {t("buttons.download_report")}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto">
                <div className="flex flex-col gap-3">
                  <div className="flex gap-2">
                    <InputDate label={t("fields.start_date")} date={relInicio} setDate={setRelInicio} />
                    <InputDate label={t("fields.end_date")} date={relFim} setDate={setRelFim} />
                  </div>
                  <button
                    onClick={baixarRelatorio}
                    disabled={!relInicio || !relFim}
                    className="flex h-9 items-center justify-center gap-2 rounded-lg bg-brand-sidebar px-4 text-sm font-semibold text-white disabled:opacity-40"
                  >
                    <Download className="h-4 w-4" />
                    {t("buttons.download")}
                  </button>
                </div>
              </PopoverContent>
            </Popover>

            <button
              onClick={() => setNovaCotacao(true)}
              className="flex h-9 items-center gap-2 rounded-lg bg-brand-sidebar px-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              <Plus className="h-4 w-4" />
              Nova cotação
            </button>
          </>
        }
      >
        {/* ABAS */}
        <div className="flex items-center gap-1 border-b border-slate-100 px-6">
          {ABAS.map((a) => {
            const n = contarAba(a.status);
            const ativa = aba === a.chave;
            return (
              <button
                key={a.chave}
                onClick={() => setAba(a.chave)}
                className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm transition-colors ${
                  ativa
                    ? "border-brand-sidebar font-semibold text-slate-900"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                {a.label}
                {n !== null && (
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                      ativa ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {n}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex h-[480px]">
          {/* LISTA */}
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-center gap-2 px-6 py-3">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && setBuscaAplicada(search)}
                  onBlur={() => setBuscaAplicada(search)}
                  placeholder="Comprador, pedido ou rastreio"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none transition-colors placeholder:text-slate-400 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
                />
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <button className="flex h-9 shrink-0 items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm text-slate-600 transition-colors hover:bg-slate-50">
                    <CalendarDays className="h-4 w-4 text-slate-400" />
                    {rotuloData}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto">
                  <div className="flex flex-col gap-3">
                    <div className="flex gap-2">
                      <InputDate label="Depois de" date={inicio} setDate={setInicio} />
                      <InputDate label="Antes de" date={fim} setDate={setFim} />
                    </div>
                    {(inicio || fim) && (
                      <button
                        onClick={() => {
                          setInicio(undefined);
                          setFim(undefined);
                        }}
                        className="text-xs text-slate-500 hover:text-slate-700"
                      >
                        Limpar período
                      </button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              <label className="flex h-9 shrink-0 items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm text-slate-600">
                <Truck className="h-4 w-4 text-slate-400" />
                <span className="text-slate-400">Transportadora</span>
                <select
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  className="cursor-pointer bg-transparent text-sm font-medium text-slate-700 outline-none"
                >
                  <option value="">Todas</option>
                  <option value="FEDEX">FedEx</option>
                  <option value="UPS">UPS</option>
                </select>
              </label>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
              {carregando && !pedidos.length ? (
                <Esqueleto />
              ) : !pedidos.length ? (
                <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 py-16 text-center">
                  <PackageSearch size={32} strokeWidth={1.5} className="text-slate-300" />
                  <p className="text-sm font-medium text-slate-500">
                    {temFiltro ? "Nenhuma cotação com esses filtros" : "Nenhuma cotação nesta aba"}
                  </p>
                </div>
              ) : (
                <table className="w-full border-collapse text-left">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                      <th className="py-2 font-semibold">Pedido</th>
                      <th className="py-2 font-semibold">Comprador</th>
                      <th className="py-2 font-semibold">Rastreio</th>
                      <th className="py-2 text-right font-semibold">Valor</th>
                      <th className="py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidos.map((p) => (
                      <LinhaCotacao
                        key={p.cotacao_id}
                        pedido={p}
                        selecionada={p.cotacao_id === selecionadaId}
                        onSelecionar={() => setSelecionadaId(p.cotacao_id)}
                      />
                    ))}
                  </tbody>
                </table>
              )}

              {total > limit && (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-slate-400">
                    Página {page} de {totalPaginas}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1 || carregando}
                      className="rounded-lg border border-slate-200 px-3 py-1 text-xs transition-colors hover:bg-slate-50 disabled:opacity-30"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
                      disabled={page === totalPaginas || carregando}
                      className="rounded-lg border border-slate-200 px-3 py-1 text-xs transition-colors hover:bg-slate-50 disabled:opacity-30"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* PAINEL */}
          <aside className="w-[300px] shrink-0 overflow-y-auto border-l border-slate-100 bg-slate-50/50 px-5 py-4">
            {selecionada ? (
              <PainelCotacao
                pedido={selecionada}
                safeCliente={safeCliente}
                onMudou={carregar}
              />
            ) : (
              <p className="pt-10 text-center text-xs text-slate-400">
                Selecione uma cotação para ver os detalhes
              </p>
            )}
          </aside>
        </div>
      </ModalBase>

      {/* Renderizado aqui, e não no Header, para o botão "Nova cotação" não
          depender de um canal de eventos entre modais. */}
      <ModalRealizarCotacao
        show={novaCotacao}
        setShow={setNovaCotacao}
        title="Realizar cotação"
        logo={<FileText />}
      />
    </>
  );
};

export default ModalCotacao;

/* ------------------------------------------------------------------ */
/* Linha da tabela                                                     */
/* ------------------------------------------------------------------ */

const LinhaCotacao = ({
  pedido,
  selecionada,
  onSelecionar,
}: {
  pedido: PedidoTabela;
  selecionada: boolean;
  onSelecionar: () => void;
}) => {
  const href = urlRastreio(pedido.carrier, pedido.tracking_number);
  const cor = corTransportadora(pedido.carrier);

  return (
    <tr
      onClick={onSelecionar}
      className={`cursor-pointer border-t border-slate-100 transition-colors ${
        selecionada ? "bg-slate-50" : "hover:bg-slate-50/60"
      }`}
    >
      <td className="relative py-3 pr-3">
        {selecionada && (
          <span className="absolute inset-y-0 left-0 w-0.5 rounded-full bg-brand-sidebar" />
        )}
        <p className="text-sm font-semibold text-slate-900">{pedido.pedido_ref}</p>
        <p className="text-[11px] text-slate-400">{dataCurta(pedido.criado_em)}</p>
      </td>

      <td className="py-3 pr-3">
        <p className="truncate text-sm text-slate-700">{pedido.comprador}</p>
        {pedido.pais && <p className="truncate text-[11px] text-slate-400">{pedido.pais}</p>}
      </td>

      <td className="py-3 pr-3">
        <div className="flex items-center gap-1.5">
          {pedido.carrier && (
            <span
              className="rounded px-1 py-0.5 text-[9px] font-bold text-white"
              style={{ backgroundColor: cor ?? "#64748b" }}
            >
              {pedido.carrier === "FEDEX" ? "FX" : pedido.carrier.slice(0, 3)}
            </span>
          )}
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="truncate font-mono text-xs font-semibold hover:underline"
              style={cor ? { color: cor } : undefined}
            >
              {pedido.tracking_number}
            </a>
          ) : (
            <span className="truncate font-mono text-xs text-slate-400">
              {pedido.tracking_number}
            </span>
          )}
        </div>
      </td>

      <td className="py-3 pr-3 text-right">
        <p className="whitespace-nowrap text-sm font-semibold tabular-nums text-slate-900">
          {usd(pedido.frete_usd + pedido.desembaraco_usd + (pedido.imposto_usd ?? 0))}
        </p>
        <p className="whitespace-nowrap text-[11px] text-slate-400">frete + desembaraço</p>
      </td>

      <td className="py-3">
        <StatusBadge status={pedido.status} />
        {pedido.ultimo_evento && (
          <p className="mt-0.5 truncate text-[11px] text-slate-400">{pedido.ultimo_evento}</p>
        )}
      </td>
    </tr>
  );
};

/* ------------------------------------------------------------------ */
/* Painel de detalhe                                                   */
/* ------------------------------------------------------------------ */

const PainelCotacao = ({
  pedido,
  safeCliente,
  onMudou,
}: {
  pedido: PedidoTabela;
  safeCliente: string;
  onMudou: () => Promise<void>;
}) => {
  const [copiado, setCopiado] = useState(false);
  const [confirmandoCancelar, setConfirmandoCancelar] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [dataColeta, setDataColeta] = useState<Date>();
  const [agendando, setAgendando] = useState(false);
  const [anexando, setAnexando] = useState(false);
  const [gerando, setGerando] = useState(false);
  const inputInvoice = React.useRef<HTMLInputElement>(null);

  const [gerandoCpsc, setGerandoCpsc] = useState(false);
  const [painelCpsc, setPainelCpsc] = useState(false);
  /*
   * As respostas do formulário CPSC.
   *
   * Os padrões são os da via que passou no desembaraço: disclaim pela opção
   * B e intended use code "N/A". A descrição fica vazia porque, em branco, o
   * gerador usa a descrição do próprio item.
   */
  const [cpsc, setCpsc] = useState<CpscOpcoes>({
    disclaimer: "B",
    intendedUseCode: "N/A",
    intendedUseDescricao: "",
  });

  // Trocar de cotação no painel não pode carregar a confirmação da anterior.
  useEffect(() => {
    setConfirmandoCancelar(false);
    setDataColeta(undefined);
    setCopiado(false);
    // O painel do CPSC carrega respostas de uma declaração: trocar de
    // cotação com ele aberto emitiria o formulário de um envio com o que foi
    // respondido para outro.
    setPainelCpsc(false);
    setCpsc({ disclaimer: "B", intendedUseCode: "N/A", intendedUseDescricao: "" });
  }, [pedido.cotacao_id]);

  const copiar = () => {
    navigator.clipboard.writeText(pedido.tracking_number).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    });
  };

  const baixar = async (tipo: "invoice" | "etiqueta" | "cpsc") => {
    try {
      const blob =
        tipo === "invoice"
          ? await baixarInvoiceCotacao(pedido.cotacao_id)
          : tipo === "cpsc"
            ? await baixarCpscCotacao(pedido.cotacao_id)
            : await baixarEtiquetaCotacao(pedido.cotacao_id);

      // Invoice e CPSC são sempre PDF. Etiqueta não: etiqueta_mime aceita
      // application/pdf e image/png, então fixar ".pdf" renomearia um PNG.
      const nome =
        tipo === "invoice"
          ? `${pedido.pedido_ref}-IN-${safeCliente}.pdf`
          : tipo === "cpsc"
            ? `${pedido.pedido_ref}-CPSC-${safeCliente}.pdf`
            : `${pedido.pedido_ref}-ET-${safeCliente}`;

      downloadArquivo(blob, nome);
    } catch (err: any) {
      // 402 = remessa não paga. Mensagem genérica aqui parecia bug.
      if (err?.response?.status === 402) {
        toast.error(
          "Documento liberado após o pagamento da remessa. Pague pela carteira ou pelo boleto.",
        );
        return;
      }
      toast.error(`Erro ao baixar ${tipo}`);
    }
  };

  /*
   * Emitir a invoice pela Intrex.
   *
   * A invoice comercial e documento do exportador, nao da transportadora.
   * Quando a FedEx nao devolve o PDF (envio para a Colombia veio com
   * shipmentDocuments vazio quatro vezes seguidas), nao ha de onde baixar: o
   * portal dela tambem nao tem o que ela nao gerou. Entao a Intrex emite, com
   * os mesmos dados que foram declarados na emissao.
   */
  const gerarInvoice = async () => {
    setGerando(true);
    try {
      await gerarInvoiceCotacao(pedido.cotacao_id);
      toast.success("Invoice emitida.");
      onMudou();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error || "Nao foi possivel emitir a invoice.",
      );
    } finally {
      setGerando(false);
    }
  };

  /*
   * Emitir o formulário CPSC.
   *
   * Regerar é permitido e sobrescreve o anterior: diferente da invoice, este
   * formulário é sempre nosso, e corrigir uma resposta (o campo 11, por
   * exemplo) não pode exigir cotação nova.
   */
  const gerarCpsc = async () => {
    setGerandoCpsc(true);
    try {
      const { data } = await gerarCpscCotacao(pedido.cotacao_id, cpsc);
      toast.success(
        data.formularios > 1
          ? `Formulário CPSC emitido — ${data.formularios} vias, uma por item.`
          : "Formulário CPSC emitido.",
      );
      setPainelCpsc(false);
      onMudou();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error || "Não foi possível emitir o formulário CPSC.",
      );
    } finally {
      setGerandoCpsc(false);
    }
  };

  /*
   * Anexar a invoice na mao.
   *
   * A FedEx gera a invoice comercial junto com a etiqueta, mas nem sempre ela
   * volta na resposta do despacho (aconteceu 1x em 364 envios). Quando falta,
   * o envio ja esta criado e a etiqueta ja saiu: nao da para reemitir sem
   * gerar uma segunda remessa e uma segunda cobranca. A invoice existe no
   * portal da transportadora, e este botao a traz para dentro da Intrex, para
   * o cliente baixar daqui como nos outros envios.
   */
  const anexarInvoice = async (arquivo: File) => {
    if (arquivo.type !== "application/pdf") {
      toast.error("A invoice precisa ser um PDF.");
      return;
    }
    // O corpo vai em JSON; um PDF de invoice tem poucas centenas de KB, mas
    // base64 cresce 1/3 e o limite do express e 50mb.
    if (arquivo.size > 8 * 1024 * 1024) {
      toast.error("PDF acima de 8 MB.");
      return;
    }

    setAnexando(true);
    try {
      const base64 = await new Promise<string>((ok, falhou) => {
        const leitor = new FileReader();
        leitor.onerror = () => falhou(leitor.error);
        // readAsDataURL devolve "data:application/pdf;base64,XXXX";
        // o backend espera so o XXXX.
        leitor.onload = () => ok(String(leitor.result).split(",")[1] || "");
        leitor.readAsDataURL(arquivo);
      });

      if (!base64) throw new Error("PDF vazio");

      await anexarDocsCotacao(pedido.cotacao_id, {
        invoice_base64: base64,
        invoice_mime: "application/pdf",
      });

      toast.success("Invoice anexada.");
      onMudou();
    } catch {
      toast.error("Nao foi possivel anexar a invoice.");
    } finally {
      setAnexando(false);
    }
  };

  const agendar = async () => {
    if (!dataColeta) return;
    setAgendando(true);
    try {
      await agendarColetaCotacao(pedido.cotacao_id, {
        pickupDate: dateToISO(dataColeta),
      });
      toast.success("Coleta agendada");
      setDataColeta(undefined);
      await onMudou();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Não foi possível agendar a coleta");
    } finally {
      setAgendando(false);
    }
  };

  const cancelar = async () => {
    setCancelando(true);
    try {
      const { data } = await excluirCotacao(pedido.cotacao_id);
      const estorno = Number(data?.estorno_centavos) || 0;

      toast.success("Cotação cancelada", {
        description: estorno
          ? `${(estorno / 100).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            })} voltaram para a sua carteira.`
          : undefined,
      });
      await onMudou();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Não foi possível cancelar a cotação");
    } finally {
      setCancelando(false);
      setConfirmandoCancelar(false);
    }
  };

  /*
   * Quando o documento pode ser baixado.
   *
   * A trava de pagamento só faz sentido ANTES do despacho. Depois que a
   * transportadora coletou, a etiqueta já está em uso e o pacote está
   * voando: negar a invoice aí só impede o cliente de reimprimir o documento
   * que a alfândega está pedindo. Mesma regra do backend.
   */
  const cancelada = pedido.status === "CANCELADO";
  const aguardandoDespacho = pedido.status === "CRIADO";
  const precisaPagar = aguardandoDespacho && !pedido.pago;

  /*
   * Só cancelada bloqueia o documento.
   *
   * A trava por pagamento foi removida em 2026-10-26: ela deixava o cliente
   * sem a invoice que a alfândega pede, inclusive com o pacote já a caminho.
   * O pagamento continua disponível aqui embaixo — só não prende mais o
   * documento. Mesma regra do servidor.
   */
  const bloqueioDoc = cancelada ? "Cotação cancelada" : null;

  const passos = passosAcompanhamento(pedido);
  const ehUps = pedido.carrier === "UPS";

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="flex items-start justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Pedido {pedido.pedido_ref}
          </p>
          <StatusBadge status={pedido.status} />
        </div>
        <h3 className="mt-1 text-base font-semibold leading-tight text-slate-900">
          {pedido.comprador}
        </h3>
        <p className="mt-0.5 text-xs leading-snug text-slate-500">
          {[pedido.pais, nomeServico(pedido.carrier, pedido.service_code)]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>

      {pedido.tracking_number !== "—" && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Rastreio</p>
            <p className="truncate font-mono text-xs font-semibold text-slate-800">
              {pedido.tracking_number}
            </p>
          </div>
          <button
            onClick={copiar}
            className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-brand-primary transition-colors hover:bg-blue-50"
          >
            {copiado ? "Copiado" : "Copiar"}
          </button>
        </div>
      )}

      <dl className="space-y-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs">
        <Linha termo="Frete" valor={usd(pedido.frete_usd)} />
        <Linha termo="Desembaraço" valor={usd(pedido.desembaraco_usd)} />
        <Linha
          termo="Impostos do destino"
          valor={
            pedido.imposto_usd == null
              ? "não estimado"
              : `${usd(pedido.imposto_usd)}${pedido.imposto_cobrado ? "" : " (estimado)"}`
          }
          destaque={pedido.imposto_usd == null ? "text-amber-600" : undefined}
        />
        <div className="flex items-center justify-between border-t border-slate-100 pt-1.5">
          <dt className="font-semibold text-slate-700">Total</dt>
          <dd className="font-semibold tabular-nums text-slate-900">
            {usd(pedido.frete_usd + pedido.desembaraco_usd + (pedido.imposto_usd ?? 0))}
          </dd>
        </div>
      </dl>

      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          Acompanhamento
        </p>
        <ol className="space-y-2.5">
          {passos.map((passo, i) => (
            <li key={i} className="flex gap-2.5">
              <span
                className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                  passo.concluido ? "bg-brand-primary" : "bg-slate-300"
                }`}
              />
              <div className="min-w-0">
                <p
                  className={`text-xs leading-tight ${
                    passo.concluido ? "font-medium text-slate-800" : "text-slate-400"
                  }`}
                >
                  {passo.label}
                </p>
                {(passo.quando || passo.detalhe) && (
                  <p className="text-[11px] leading-tight text-slate-400">
                    {[passo.quando, passo.detalhe].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {precisaPagar && (
        <PainelPagamento pedido={pedido} onPago={onMudou} />
      )}

      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-3 gap-2">
          <BotaoDoc
            icone={<Tag className="h-3.5 w-3.5" />}
            label="Etiqueta"
            titulo={
              !pedido.documento_ETQ
                ? "Etiqueta ainda não emitida"
                : (bloqueioDoc ??
                  (pedido.volumes > 1
                    ? `${pedido.volumes} volumes em 1 PDF`
                    : "Baixar etiqueta"))
            }
            desabilitado={!pedido.documento_ETQ || Boolean(bloqueioDoc)}
            onClick={() => baixar("etiqueta")}
            selo={pedido.volumes > 1 ? pedido.volumes : undefined}
          />
          <BotaoDoc
            icone={<FileText className="h-3.5 w-3.5" />}
            label="Invoice"
            titulo={
              !pedido.documento_INV
                ? "Invoice ainda não emitida"
                : (bloqueioDoc ?? "Baixar invoice")
            }
            desabilitado={!pedido.documento_INV || Boolean(bloqueioDoc)}
            onClick={() => baixar("invoice")}
          />
          <BotaoDoc
            icone={<ShieldCheck className="h-3.5 w-3.5" />}
            label="CPSC"
            titulo={
              !pedido.documento_CPSC
                ? "Formulário CPSC ainda não emitido"
                : (bloqueioDoc ?? "Baixar formulário CPSC")
            }
            desabilitado={!pedido.documento_CPSC || Boolean(bloqueioDoc)}
            onClick={() => baixar("cpsc")}
          />
        </div>

        {/* Formulário CPSC.
            Fica sempre disponível, e não só quando falta — é o papel que o
            despachante pede no desembaraço, e o ponto todo é tê-lo em mãos
            antes de precisar. Abre um painel em vez de emitir direto porque
            duas respostas não saem da cotação e são declaração de quem
            assina. */}
        {!cancelada && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[11px] font-medium text-slate-800">
                  Formulário CPSC
                  {pedido.documento_CPSC && (
                    <span className="ml-1.5 font-normal text-slate-500">· já emitido</span>
                  )}
                </p>
                <p className="mt-0.5 text-[11px] leading-snug text-slate-600">
                  Formulário da FedEx que declara que a mercadoria não tem
                  registro na CPSC. Sai pré-preenchido com o tracking, a
                  descrição do item e os dados do cadastro — uma via por item.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPainelCpsc((v) => !v)}
                className="shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 transition hover:bg-slate-100"
              >
                {painelCpsc
                  ? "Fechar"
                  : pedido.documento_CPSC
                    ? "Emitir de novo"
                    : "Emitir"}
              </button>
            </div>

            {painelCpsc && (
              <div className="mt-3 space-y-3 border-t border-slate-200 pt-3">
                <div>
                  <p className="text-[11px] font-medium text-slate-700">
                    Código de disclaim (campo 2)
                  </p>
                  <div className="mt-1.5 flex flex-col gap-1">
                    {(
                      [
                        ["B", "Opção B — os dados não são exigidos"],
                        ["A", "Opção A — produto não é regulado pela agência"],
                        ["NAO_COMERCIAL", "Transação não comercial"],
                      ] as const
                    ).map(([valor, texto]) => (
                      <label key={valor} className="flex items-center gap-2 text-[11px] text-slate-700">
                        <input
                          type="radio"
                          name={`cpsc-disclaimer-${pedido.cotacao_id}`}
                          checked={cpsc.disclaimer === valor}
                          onChange={() => setCpsc((o) => ({ ...o, disclaimer: valor }))}
                          className="h-3 w-3"
                        />
                        {texto}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="text-[11px] text-slate-700">
                    Intended Use Code
                    <input
                      value={cpsc.intendedUseCode ?? ""}
                      onChange={(e) => setCpsc((o) => ({ ...o, intendedUseCode: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-[11px]"
                    />
                  </label>
                  <label className="text-[11px] text-slate-700">
                    Description
                    <input
                      value={cpsc.intendedUseDescricao ?? ""}
                      onChange={(e) =>
                        setCpsc((o) => ({ ...o, intendedUseDescricao: e.target.value }))
                      }
                      placeholder="em branco: usa a descrição do item"
                      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-[11px]"
                    />
                  </label>
                </div>

                <p className="text-[10px] leading-snug text-slate-500">
                  Com disclaim, o formulário manda pular para a Parte V — as
                  Partes II a IV saem vazias, como na via que passou no
                  desembaraço. O campo 11 (produto infantil) e a assinatura
                  ficam para a caneta.
                </p>

                <button
                  type="button"
                  disabled={gerandoCpsc}
                  onClick={gerarCpsc}
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 px-2.5 py-1.5 text-[11px] font-medium text-white transition hover:bg-slate-900 disabled:opacity-60"
                >
                  {gerandoCpsc ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-3 w-3" />
                  )}
                  {gerandoCpsc ? "Emitindo..." : "Emitir formulário CPSC"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Envio despachado e sem invoice: ela nao voltou da transportadora.
            Nao e estado normal, por isso o aviso em vez de so um botao a
            mais — quem atende o cliente precisa saber de onde tirar o PDF. */}
        {pedido.documento_ETQ && !pedido.documento_INV && !cancelada && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-[11px] font-medium text-amber-900">
              Este envio saiu sem a invoice
            </p>
            <p className="mt-0.5 text-[11px] leading-snug text-amber-700">
              A transportadora não devolveu o PDF. A Intrex emite a invoice com
              os mesmos dados declarados no envio, ou você anexa a sua.
            </p>
            <input
              ref={inputInvoice}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => {
                const arquivo = e.target.files?.[0];
                // Limpa o valor: sem isso, escolher o mesmo arquivo de novo
                // (depois de uma falha) nao dispara onChange.
                e.target.value = "";
                if (arquivo) anexarInvoice(arquivo);
              }}
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={gerando || anexando}
                onClick={gerarInvoice}
                className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-2.5 py-1.5 text-[11px] font-medium text-white transition hover:bg-amber-700 disabled:opacity-60"
              >
                {gerando ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <FileText className="h-3 w-3" />
                )}
                {gerando ? "Emitindo..." : "Emitir invoice pela Intrex"}
              </button>
              <button
                type="button"
                disabled={anexando || gerando}
                onClick={() => inputInvoice.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-[11px] font-medium text-amber-800 transition hover:bg-amber-100 disabled:opacity-60"
              >
                {anexando ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Upload className="h-3 w-3" />
                )}
                {anexando ? "Anexando..." : "Anexar a minha"}
              </button>
            </div>
          </div>
        )}

        {/* O endpoint de coleta por cotação só atende UPS — o handler recusa
            FedEx com 400. Botão desabilitado com o motivo é melhor do que
            deixar a pessoa clicar e tomar erro. */}
        {ehUps ? (
          <div className="flex flex-col gap-2">
            <InputDate label="Data da coleta" date={dataColeta} setDate={setDataColeta} />
            <button
              onClick={agendar}
              disabled={!dataColeta || agendando}
              className="flex h-10 items-center justify-center gap-2 rounded-lg bg-brand-sidebar text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
            >
              <Truck className="h-4 w-4" />
              {agendando ? "Agendando…" : "Agendar coleta"}
            </button>
          </div>
        ) : (
          <button
            disabled
            title="Coleta FedEx é agendada pelo item “Agendar coleta” no menu"
            className="flex h-10 cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-brand-sidebar text-sm font-semibold text-white opacity-40"
          >
            <Truck className="h-4 w-4" />
            Agendar coleta
          </button>
        )}

        {confirmandoCancelar ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-2.5">
            <p className="mb-2 text-[11px] leading-snug text-red-800">
              {pedido.pago ? (
                <>
                  O valor pago volta como saldo na carteira. Mas a etiqueta{" "}
                  <strong>não</strong> é cancelada na {pedido.carrier} e continua
                  válida — descarte a que já tiver impresso.
                </>
              ) : (
                <>
                  A cotação passa a constar como cancelada. A etiqueta, se já emitida,{" "}
                  <strong>não</strong> é cancelada na {pedido.carrier}.
                </>
              )}
            </p>
            <div className="flex gap-2">
              <button
                onClick={cancelar}
                disabled={cancelando}
                className="flex h-8 flex-1 items-center justify-center gap-1 rounded-md bg-red-600 text-xs font-semibold text-white disabled:opacity-40"
              >
                <Check className="h-3.5 w-3.5" />
                {cancelando ? "Cancelando…" : "Confirmar"}
              </button>
              <button
                onClick={() => setConfirmandoCancelar(false)}
                className="h-8 flex-1 rounded-md border border-slate-200 bg-white text-xs text-slate-600"
              >
                Voltar
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setConfirmandoCancelar(true)}
            className="text-center text-xs font-medium text-red-600 transition-colors hover:text-red-700"
          >
            Cancelar cotação
          </button>
        )}
      </div>
    </div>
  );
};


/**
 * Pagar a remessa sem sair da lista.
 *
 * Estava só no Estágio 3 da cotação: quem fechava a janela antes de pagar
 * não tinha por onde voltar, e a cotação ficava parada sem documento.
 */
const PainelPagamento = ({
  pedido,
  onPago,
}: {
  pedido: PedidoTabela;
  onPago: () => Promise<void>;
}) => {
  const [cobranca, setCobranca] = useState<CobrancaCotacao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [pagando, setPagando] = useState<"saldo" | "boleto" | null>(null);

  useEffect(() => {
    setCarregando(true);
    getCobrancaCotacao(pedido.cotacao_id)
      .then(setCobranca)
      .catch(() => setCobranca(null))
      .finally(() => setCarregando(false));
  }, [pedido.cotacao_id]);

  if (carregando) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-xs text-slate-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Carregando a cobrança…
      </div>
    );
  }

  // Fora da allowlist a remessa não passa por cobrança: nada a mostrar.
  if (!cobranca || cobranca.cobranca_habilitada === false) return null;

  const brl = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const pagarSaldo = async () => {
    setPagando("saldo");
    try {
      await pagarCotacaoComSaldo(pedido.cotacao_id);
      toast.success("Remessa paga com o saldo da carteira");
      avisarCarteiraAtualizada();
      await onPago();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Não foi possível pagar com o saldo");
    } finally {
      setPagando(null);
    }
  };

  const gerarBoleto = async () => {
    setPagando("boleto");
    try {
      const vencimento = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
      const resp = await criarBoletoCotacao(pedido.cotacao_id, vencimento);
      const url = resp?.boleto?.bankSlipUrl;
      toast.success(url ? "Boleto gerado — abrindo em nova aba" : "Boleto gerado");
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      await onPago();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Não foi possível gerar o boleto");
    } finally {
      setPagando(null);
    }
  };

  // Boleto emitido e ainda não compensado: gerar outro cobraria duas vezes.
  if (pedido.boleto_pendente) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
        <p className="text-[11px] leading-relaxed text-amber-900">
          Boleto de <strong>{brl(cobranca.total_brl)}</strong> gerado, aguardando
          compensação. O documento libera assim que o banco confirmar.
        </p>
        {cobranca.pagamento?.boleto_url && (
          <a
            href={cobranca.pagamento.boleto_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 inline-block text-[11px] font-semibold text-amber-800 underline"
          >
            Abrir o boleto
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-brand-sidebar px-3 py-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold text-slate-800">Pagar a remessa</p>
        <p className="text-sm font-bold text-slate-900">{brl(cobranca.total_brl)}</p>
      </div>

      <p className="mt-0.5 text-[11px] leading-snug text-slate-500">
        Frete, desembaraço
        {cobranca.imposto_cobravel ? " e impostos do destino" : ""}. A etiqueta e a
        invoice liberam depois do pagamento.
      </p>

      <div className="mt-2.5 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={pagarSaldo}
          disabled={pagando !== null || !cobranca.saldo_suficiente}
          title={
            cobranca.saldo_suficiente
              ? `Saldo de ${brl(cobranca.saldo_centavos / 100)}`
              : `Saldo insuficiente (${brl(cobranca.saldo_centavos / 100)})`
          }
          className="flex h-9 items-center justify-center gap-1.5 rounded-lg bg-brand-sidebar text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
        >
          {pagando === "saldo" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Wallet className="h-3.5 w-3.5" />
          )}
          Pagar com saldo
        </button>

        <button
          type="button"
          onClick={gerarBoleto}
          disabled={pagando !== null}
          className="flex h-9 items-center justify-center gap-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-40"
        >
          {pagando === "boleto" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Receipt className="h-3.5 w-3.5" />
          )}
          Gerar boleto
        </button>
      </div>

      {!cobranca.saldo_suficiente && (
        <p className="mt-1.5 text-[11px] text-slate-400">
          Saldo atual {brl(cobranca.saldo_centavos / 100)}.{" "}
          <button
            type="button"
            onClick={abrirCarteira}
            className="font-semibold text-brand-primary underline underline-offset-2"
          >
            Adicionar saldo
          </button>
        </p>
      )}
    </div>
  );
};

const Linha = ({
  termo,
  valor,
  destaque,
}: {
  termo: string;
  valor: string;
  destaque?: string;
}) => (
  <div className="flex items-center justify-between gap-2">
    <dt className="text-slate-500">{termo}</dt>
    <dd className={`tabular-nums ${destaque ?? "font-medium text-slate-800"}`}>{valor}</dd>
  </div>
);

const BotaoDoc = ({
  icone,
  label,
  titulo,
  desabilitado,
  onClick,
  selo,
}: {
  icone: React.ReactNode;
  label: string;
  titulo: string;
  desabilitado: boolean;
  onClick: () => void;
  selo?: number;
}) => (
  <button
    title={titulo}
    disabled={desabilitado}
    onClick={onClick}
    className="relative flex h-9 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
  >
    {icone}
    {label}
    {selo && !desabilitado && (
      <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-primary px-1 text-[9px] font-bold text-white">
        {selo}
      </span>
    )}
  </button>
);

const Esqueleto = () => (
  <div className="space-y-2 pt-2">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex gap-3 border-t border-slate-100 py-3">
        {[3, 3, 3, 2, 2].map((w, j) => (
          <div key={j} className="h-3 animate-pulse rounded bg-slate-100" style={{ flex: w }} />
        ))}
      </div>
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/* Dados                                                               */
/* ------------------------------------------------------------------ */

type PedidoTabela = {
  cotacao_id: number;
  pedido_ref: string;
  tracking_number: string;
  carrier: string;
  service_code: string;
  comprador: string;
  pais: string;
  criado_em: string;
  status: string;
  ultimo_evento: string | null;
  tracking_raw: unknown;
  documento_INV: string;
  documento_ETQ: string;
  documento_CPSC: string;
  volumes: number;
  frete_usd: number;
  desembaraco_usd: number;
  imposto_usd: number | null;
  imposto_cobrado: boolean;
  pago: boolean;
  boleto_pendente: boolean;
  status_pagamento: string | null;
};

const extrairPedidos = (cotacoes: Cotacao[]): PedidoTabela[] =>
  cotacoes.map((cotacao) => {
    const evento = extrairUltimoEvento(cotacao.tracking_raw);

    return {
      cotacao_id: cotacao.id,
      pedido_ref: cotacao.pedido_ref,
      tracking_number: cotacao.tracking_number ?? "—",
      carrier: cotacao.carrier ?? "",
      service_code: cotacao.serviceCode ?? "",
      comprador: cotacao.pedido?.nomeComprador ?? cotacao.nomeComprador ?? "—",
      pais: cotacao.pedido?.pais ?? cotacao.pais_dest ?? "",
      criado_em: cotacao.createdAt,
      status: cotacao.status_norm,
      ultimo_evento: evento
        ? [evento.descricao, evento.local, tempoRelativo(evento.quando)]
            .filter(Boolean)
            .join(" · ")
        : null,
      tracking_raw: cotacao.tracking_raw,
      documento_INV: cotacao.invoice_path ?? "",
      documento_ETQ: cotacao.etiqueta_path ?? "",
      documento_CPSC: cotacao.cpsc_path ?? "",
      // Cotação antiga guardava `caixa` como objeto, não lista.
      volumes: Array.isArray(cotacao.caixa) ? cotacao.caixa.length || 1 : 1,
      frete_usd: Number(cotacao.preco_final) || 0,
      desembaraco_usd: Number(cotacao.desembaraco_usd) || 0,
      imposto_usd:
        cotacao.imposto_estimado_usd != null ? Number(cotacao.imposto_estimado_usd) : null,
      imposto_cobrado: cotacao.imposto_cobrado === true,
      pago: cotacao.pago === true,
      boleto_pendente: cotacao.boleto_pendente === true,
      status_pagamento: cotacao.status_pagamento ?? null,
    };
  });

export const downloadArquivo = (blob: Blob, filename?: string) => {
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename ?? "arquivo";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(blobUrl);
};
